import { styled } from '@mui/material/styles';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Table,
  TableCell,
  TableRow,
  TableContainer,
  TextField,
} from '@mui/material';
import { Link } from 'react-router-dom';

// ─── Page wrapper ─────────────────────────────────────────────────────────────

export const ReportPageWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#fdfdfd',
}));

// ─── Filter section ───────────────────────────────────────────────────────────

export const FilterPaper = styled(Paper)({
  padding: '24px',
  marginBottom: '32px',
  border: '1px solid #eee',
  borderRadius: '8px',
  boxShadow: 'none',
});

// ─── Filter action row ────────────────────────────────────────────────────────

export const FilterActionsBox = styled(Box)({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
});

export const FilterActionsBoxSmallGap = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
});

export const FlexSpacer = styled(Box)({
  flexGrow: 1,
});

// ─── Generate button ──────────────────────────────────────────────────────────

export const GenerateButton = styled(Button)({
  paddingLeft: '24px',
  paddingRight: '24px',
  paddingTop: '8px',
  paddingBottom: '8px',
  textTransform: 'none',
  fontWeight: 600,
  height: '40px',
});

// ─── Export icon buttons ──────────────────────────────────────────────────────

export const PdfIconButton = styled(IconButton)({
  color: 'var(--error)',
  backgroundColor: 'rgba(192, 57, 43, 0.05)',
  borderRadius: '8px',
  width: '40px',
  height: '40px',
  border: '1px solid rgba(192, 57, 43, 0.2)',
  '&:hover': {
    backgroundColor: 'rgba(192, 57, 43, 0.1)',
  },
});

export const CsvIconButton = styled(IconButton)({
  color: 'var(--success)',
  backgroundColor: 'rgba(39, 174, 96, 0.05)',
  borderRadius: '8px',
  width: '40px',
  height: '40px',
  border: '1px solid rgba(39, 174, 96, 0.2)',
  '&:hover': {
    backgroundColor: 'rgba(39, 174, 96, 0.1)',
  },
});

// ─── Loading state ────────────────────────────────────────────────────────────

export const LoadingBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  padding: '40px',
});

// ─── Summary stat cards ───────────────────────────────────────────────────────

export const SummaryGrid = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

export const StatCard = styled(Paper)({
  padding: '20px',
  border: '1px solid #eee',
  borderRadius: '8px',
  backgroundColor: '#f8fbfc',
  boxShadow: 'none',
});

export const StatLabel = styled(Typography)({
  textTransform: 'uppercase',
  marginBottom: '4px',
  fontWeight: 600,
});

export const StatValue = styled(Typography)({
  fontWeight: 700,
});

export const StatValueNavy = styled(Typography)({
  fontWeight: 700,
  color: 'var(--primary-navy)',
});

export const StatValueBlue = styled(Typography)({
  fontWeight: 700,
  color: 'var(--primary-blue)',
});

export const StatValueError = styled(Typography)({
  fontWeight: 700,
  color: 'var(--error)',
});

export const StatValueSuccess = styled(Typography)({
  fontWeight: 700,
  color: 'var(--success)',
});

// ─── Table ────────────────────────────────────────────────────────────────────

export const StyledTable = styled(Table)({
  minWidth: 650,
});

export const BorderlessRow = styled(TableRow)({
  '&:last-child td, &:last-child th': { border: 0 },
});

export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid var(--border)',
  boxShadow: 'none',
  marginTop: theme.spacing(2),
}));

export const HeaderCell = styled(TableCell)({
  fontWeight: 600,
  color: 'var(--subtext)',
  textTransform: 'uppercase',
  fontSize: '12px',
  backgroundColor: 'var(--bg-color)',
  borderBottom: '1px solid var(--border)',
});

export const DataCell = styled(TableCell)({
  fontSize: '14px',
  color: 'var(--primary-navy)',
  padding: '16px',
  borderBottom: '1px solid var(--border)',
});

export const DataCellBold = styled(TableCell)({
  fontSize: '14px',
  color: 'var(--primary-navy)',
  padding: '16px',
  borderBottom: '1px solid var(--border)',
  fontWeight: 600,
});

export const DataCellMedium = styled(TableCell)({
  fontSize: '14px',
  color: 'var(--primary-navy)',
  padding: '16px',
  borderBottom: '1px solid var(--border)',
  fontWeight: 500,
});

export const DataCellBlue = styled(TableCell)({
  fontSize: '14px',
  padding: '16px',
  borderBottom: '1px solid var(--border)',
  color: 'var(--primary-blue)',
  fontWeight: 500,
});

export const DataCellError = styled(TableCell)({
  fontSize: '14px',
  padding: '16px',
  borderBottom: '1px solid var(--border)',
  color: 'var(--error)',
  fontWeight: 600,
});

/** Conditionally error-coloured cell (positive balance = error colour). */
export const DataCellBalance = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'positive',
})<{ positive?: boolean }>(({ positive }) => ({
  fontSize: '14px',
  padding: '16px',
  borderBottom: '1px solid var(--border)',
  color: positive ? 'var(--error)' : 'var(--primary-navy)',
  fontWeight: 600,
}));

/** Conditionally error-coloured cell for customer report (any balance > 0 triggers error). */
export const DataCellConditionalError = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'highlight',
})<{ highlight?: boolean }>(({ highlight }) => ({
  fontSize: '14px',
  padding: '16px',
  borderBottom: '1px solid var(--border)',
  color: highlight ? 'var(--error)' : 'inherit',
  fontWeight: highlight ? 600 : 400,
}));

export const EmptyCell = styled(TableCell)({
  paddingTop: '40px',
  paddingBottom: '40px',
});

// ─── Custom Inputs & Links ────────────────────────────────────────────────────

export const CustomerTextField = styled(TextField)({
  '& .MuiInputBase-input': {
    paddingLeft: '8px !important',
  },
});

export const OrderLink = styled(Link)({
  textDecoration: 'underline',
  color: '#1976d2',
});
